import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { SkeletonCard } from "@/components/Skeleton";
import CountUp from "@/components/CountUp";
// AI Insights hoisted to Home.jsx below the live board — no longer imported here.
import CreditReceivableStrip from "@/components/CreditReceivableStrip";
import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis,
  Tooltip as RechartsTooltip, CartesianGrid,
} from "recharts";
import {
  TrendingUp, TrendingDown, Wallet, AlertTriangle, ArrowRight,
  IndianRupee, Fuel, Users, Receipt, BarChart3,
} from "lucide-react";

/**
 * DealerHero — visual dashboard block for the dealer landing page.
 *
 * Shows 4 KPI cards, a 30-day sales sparkline, an expense-category top list,
 * top credit customers, and an AI insight teaser. Pulls from existing
 * `/analytics/dashboard` + `/dealer/insights` endpoints — no new backend.
 */
const fmtCompact = (n) => {
  n = Number(n) || 0;
  if (n >= 10_000_000) return `${(n / 10_000_000).toFixed(1)}Cr`;
  if (n >= 100_000) return `${(n / 100_000).toFixed(1)}L`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return Math.round(n).toLocaleString("en-IN");
};

const fmtRs = (n) => `₹${fmtCompact(n)}`;

const PERIODS = [
  { id: "today",     label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "7d",        label: "7 days" },
  { id: "30d",       label: "30 days" },
  { id: "90d",       label: "90 days" },
];

export default function DealerHero({ pump }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [prevData, setPrevData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("30d");

  // For every non-today period we compare against the equivalent prior window
  // (e.g. 7d ↔ previous 7d). "today" specifically compares against "yesterday"
  // to match the copy the delta chip shows.
  const compareTo = period === "today" ? "yesterday" : period === "yesterday" ? "today" : null;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const primary = api.get(`/analytics/dashboard?period=${period}`);
    const secondary = compareTo
      ? api.get(`/analytics/dashboard?period=${compareTo}`).catch(() => ({ data: null }))
      : Promise.resolve({ data: null });
    Promise.all([primary, secondary])
      .then(([a, b]) => { if (cancelled) return; setData(a.data); setPrevData(b.data); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period, compareTo]);

  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="dealer-hero-loading">
        <SkeletonCard /><SkeletonCard /><SkeletonCard /><SkeletonCard />
      </div>
    );
  }

  const kpis = data?.kpis || {};
  const prevKpis = prevData?.kpis || {};
  const trend = data?.trend || [];
  const topCustomers = (data?.top_credit_customers || []).slice(0, 3);
  const products = data?.products || {};

  /** Delta helper — returns `{ pct, dir }` where dir is `up|down|flat`.
   *  Only rendered when we have a comparison period AND prev value > 0
   *  (otherwise "+100% from ₹0" is misleading). */
  const deltaFor = (curKey) => {
    if (!compareTo) return null;
    const cur = Number(kpis[curKey] || 0);
    const prev = Number(prevKpis[curKey] || 0);
    if (prev === 0 && cur === 0) return null;
    if (prev === 0) return { pct: null, dir: "up", label: "new" };
    const pct = ((cur - prev) / prev) * 100;
    return {
      pct: Math.round(Math.abs(pct)),
      dir: pct > 0.5 ? "up" : pct < -0.5 ? "down" : "flat",
      label: `${pct >= 0 ? "+" : "-"}${Math.round(Math.abs(pct))}% vs ${compareTo === "yesterday" ? "yesterday" : "today"}`,
    };
  };

  return (
    <div className="space-y-4" data-testid="dealer-hero">
      {/* ============ Top strip: greeting + period switcher + language ============ */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-gradient-to-br from-primary/[0.08] via-card to-amber-500/[0.05] p-4">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            {PERIODS.find((p) => p.id === period)?.label || "30 days"}
          </div>
          <div className="mt-0.5 font-display text-xl font-bold text-foreground truncate">
            {pump?.name || "Your pump"} · <span className="text-primary">₹{fmtCompact(kpis.sales)}</span> tracked
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Period switcher — Today / Yesterday / 7d / 30d / 90d */}
          <div className="inline-flex rounded-lg border border-border bg-background p-0.5 shadow-sm" role="tablist" aria-label={t("dealer.dealerhero.time_period")} data-testid="dealer-hero-period-switcher">
            {PERIODS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPeriod(p.id)}
                aria-pressed={period === p.id}
                className={
                  "rounded-md px-2.5 py-1 text-[11px] font-semibold transition-colors " +
                  (period === p.id
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground")
                }
                data-testid={`dealer-hero-period-${p.id}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="hidden items-center gap-2 md:flex">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{t("common.language")}</span>
            <LanguageSwitcher compact />
          </div>
        </div>
      </div>

      {/* ============ Credit receivable strip (Khatabook-style) ============ */}
      <CreditReceivableStrip />

      {/* ============ 4 KPI cards ============ */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard
          testId="kpi-sales"
          icon={<IndianRupee className="h-4 w-4" />}
          label={t("dealer.dealerhero.total_sales")}
          value={<CountUp value={kpis.sales || 0} format={(n) => `₹${n.toLocaleString("en-IN")}`} />}
          sub={`${fmtCompact(kpis.litres)} L dispensed`}
          delta={deltaFor("sales")}
          tone="primary"
        />
        <KpiCard
          testId="kpi-cash"
          icon={<Wallet className="h-4 w-4" />}
          label={t("dealer.dealerhero.cash_collected")}
          value={<CountUp value={kpis.cash_submitted || 0} format={(n) => `₹${n.toLocaleString("en-IN")}`} />}
          sub={`Expected ₹${fmtCompact(kpis.cash_expected)}`}
          delta={deltaFor("cash_submitted")}
          tone="ok"
        />
        <KpiCard
          testId="kpi-credit"
          icon={<Receipt className="h-4 w-4" />}
          label={t("csa.credit_entries")}
          value={<CountUp value={kpis.credit || 0} format={(n) => `₹${n.toLocaleString("en-IN")}`} />}
          sub={`${topCustomers.length} active customers`}
          delta={deltaFor("credit")}
          tone="info"
        />
        <KpiCard
          testId="kpi-shortage"
          icon={<AlertTriangle className="h-4 w-4" />}
          label="Shortage · month"
          value={<CountUp value={kpis.shortage || 0} format={(n) => `₹${n.toLocaleString("en-IN")}`} />}
          sub={kpis.shortage > 0 ? "Needs review" : "All clean ✓"}
          delta={deltaFor("shortage")}
          deltaInverted
          tone={kpis.shortage > 0 ? "bad" : "ok"}
        />
      </div>

      {/* ============ Sales trend spans full width now (AI has its own row) ============ */}
      <div className="rounded-2xl border border-border bg-card p-4 shadow-sm" data-testid="sales-trend-card">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-primary" />
            <div className="text-sm font-bold text-foreground">{t("dealer.dealerhero.daily_sales_trend")}</div>
            <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{PERIODS.find((p) => p.id === period)?.label || "30 days"}</span>
          </div>
        </div>
        <SalesTrendChart trend={trend} />

        {/* Product mix chips */}
        {Object.keys(products).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5 border-t border-border pt-3">
            {Object.entries(products).slice(0, 4).map(([name, p]) => (
              <div key={name} className="flex items-center gap-1.5 rounded-full bg-secondary px-2.5 py-1 text-[10px] font-semibold text-foreground">
                <Fuel className="h-2.5 w-2.5 text-primary" />
                {name}
                <span className="text-muted-foreground">·</span>
                <span className="font-mono tabular-nums">{fmtCompact(p.litres || 0)}L</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AI Insights hoisted to Home.jsx (below the live board) so both cards
          get equal spotlight — used to be buried inside DealerHero. */}

      {/* ============ Expense tracker + Credit outstanding side-by-side ============ */}
      <div className="grid gap-3 lg:grid-cols-2">
        <ExpenseSummaryCard total={kpis.expenses} sales={kpis.sales} />
        <CreditCustomersCard customers={topCustomers} total={kpis.credit} />
      </div>
    </div>
  );
}

function KpiCard({ testId, icon, label, value, sub, tone = "neutral", delta, deltaInverted = false }) {
  const tones = {
    neutral: "border-border bg-card",
    primary: "border-primary/30 bg-gradient-to-br from-primary/10 to-primary/[0.02]",
    ok: "border-emerald-300/60 bg-gradient-to-br from-emerald-500/10 to-emerald-500/[0.02] dark:border-emerald-900/60",
    info: "border-sky-300/60 bg-gradient-to-br from-sky-500/10 to-sky-500/[0.02] dark:border-sky-900/60",
    bad: "border-red-300/60 bg-gradient-to-br from-red-500/10 to-red-500/[0.02] dark:border-red-900/60",
  };
  const iconTones = {
    neutral: "bg-secondary text-foreground",
    primary: "bg-primary/15 text-primary",
    ok: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
    info: "bg-sky-500/15 text-sky-700 dark:text-sky-400",
    bad: "bg-red-500/15 text-red-700 dark:text-red-400",
  };
  // For "shortage" the semantics are inverted: going UP is bad, DOWN is good.
  const isPositiveMove = delta?.dir === "flat"
    ? null
    : deltaInverted
      ? delta?.dir === "down"
      : delta?.dir === "up";
  const chipCls =
    isPositiveMove === null
      ? "bg-secondary text-muted-foreground"
      : isPositiveMove
        ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
        : "bg-red-500/15 text-red-700 dark:text-red-400";
  return (
    <div className={"rounded-2xl border-2 p-3.5 " + tones[tone]} data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className={"flex h-7 w-7 items-center justify-center rounded-lg " + iconTones[tone]}>{icon}</div>
        <div className="text-[9px] font-black uppercase tracking-widest text-muted-foreground">{label}</div>
      </div>
      <div className="mt-2 font-mono text-2xl font-black tabular-nums tracking-tight text-foreground">{value}</div>
      <div className="mt-0.5 flex flex-wrap items-center justify-between gap-1">
        <div className="text-[10px] text-muted-foreground">{sub}</div>
        {delta && (
          <span
            className={"inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-black " + chipCls}
            data-testid={`${testId}-delta`}
            title={delta.label}
          >
            {delta.dir === "up" ? <TrendingUp className="h-2.5 w-2.5" /> : delta.dir === "down" ? <TrendingDown className="h-2.5 w-2.5" /> : null}
            {delta.pct == null ? "NEW" : `${delta.dir === "up" ? "+" : delta.dir === "down" ? "-" : "±"}${delta.pct}%`}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Recharts-powered daily sales trend (Testing Notes 12, item #4).
 * X-axis shows formatted dates ("Mon 24 Feb"), Y-axis shows ₹ (compact), and the
 * hover tooltip surfaces {date, sales, litres, hisabs, top_nozzle} — the backend
 * enriches every trend row with these fields.
 */
function SalesTrendChart({ trend = [] }) {
  if (!trend || trend.length < 2) {
    return (
      <div className="mt-3 flex h-40 items-center justify-center rounded-lg border border-dashed border-border bg-secondary/30 text-xs text-muted-foreground">
        Not enough data yet — sales will chart here after a few shifts.
      </div>
    );
  }
  const first = Number(trend[0]?.sales || 0);
  const last = Number(trend[trend.length - 1]?.sales || 0);
  const delta = last - first;
  const deltaPct = first > 0 ? ((delta / first) * 100).toFixed(1) : 0;
  const up = delta >= 0;

  return (
    <div className="mt-3">
      <div className="h-40 w-full" data-testid="sales-trend-chart">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={trend} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
            <defs>
              <linearGradient id="salesTrendFill" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} opacity={0.4} />
            <XAxis
              dataKey="date"
              tickFormatter={formatShortDate}
              tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }}
              tickLine={false}
              axisLine={false}
              minTickGap={16}
            />
            <YAxis
              tickFormatter={(v) => `₹${fmtCompact(v)}`}
              tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }}
              tickLine={false}
              axisLine={false}
              width={44}
            />
            <RechartsTooltip content={<SalesTrendTooltip />} />
            <Area
              type="monotone"
              dataKey="sales"
              stroke="hsl(var(--primary))"
              strokeWidth={2}
              fill="url(#salesTrendFill)"
              activeDot={{ r: 4, fill: "hsl(var(--primary))", stroke: "hsl(var(--background))", strokeWidth: 1.5 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-1 flex items-center justify-between text-[11px] text-muted-foreground">
        <span>{formatShortDate(trend[0]?.date)}</span>
        <span className={"flex items-center gap-1 font-semibold " + (up ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400")}>
          {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
          {up ? "+" : ""}{deltaPct}%
        </span>
        <span>{formatShortDate(trend[trend.length - 1]?.date)}</span>
      </div>
    </div>
  );
}

/** Formats an ISO date (`YYYY-MM-DD`) into a short readable label like "Mon 24 Feb". */
function formatShortDate(d) {
  if (!d) return "";
  try {
    const dt = new Date(`${d}T00:00:00`);
    if (Number.isNaN(dt.getTime())) return d;
    return dt.toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" });
  } catch {
    return d;
  }
}

/** Rich tooltip surfacing date · sales · litres · hisabs · top nozzle. */
function SalesTrendTooltip({ active, payload }) {
  const { t } = useTranslation();
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload || {};
  return (
    <div
      className="rounded-lg border border-border bg-card px-3 py-2 text-xs shadow-lg"
      data-testid="sales-trend-tooltip"
    >
      <div className="mb-1 font-bold text-foreground">{formatShortDate(row.date)}</div>
      <div className="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-0.5">
        <span className="text-muted-foreground">{t("csa.sales")}</span>
        <span className="text-right font-mono font-semibold text-foreground">
          ₹{Number(row.sales || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
        </span>
        <span className="text-muted-foreground">{t("csa.litres")}</span>
        <span className="text-right font-mono text-foreground">
          {Number(row.litres || 0).toLocaleString("en-IN", { maximumFractionDigits: 1 })} L
        </span>
        <span className="text-muted-foreground">{t("setup.shifts")}</span>
        <span className="text-right font-mono text-foreground">{row.hisabs || 0}</span>
        {row.top_nozzle && (
          <>
            <span className="text-muted-foreground">{t("dealer.dealerhero.top_nozzle")}</span>
            <span className="text-right font-mono text-primary">{row.top_nozzle}</span>
          </>
        )}
      </div>
    </div>
  );
}

function ExpenseSummaryCard({ total, sales }) {
  const { t } = useTranslation();
  const percent = sales > 0 ? Math.round((total / sales) * 100) : 0;
  const zone = percent < 5 ? "ok" : percent < 15 ? "warn" : "bad";
  const zoneCls = {
    ok: "text-emerald-600 dark:text-emerald-400",
    warn: "text-amber-600 dark:text-amber-400",
    bad: "text-red-600 dark:text-red-400",
  }[zone];
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-sm" data-testid="expense-summary-card">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-amber-500/15 text-amber-700 dark:text-amber-400">
            <BarChart3 className="h-4 w-4" />
          </div>
          <div className="text-sm font-bold text-foreground">Expenses · 30d</div>
        </div>
        <Link to="/dealer/expense-analytics" className="text-[11px] font-semibold text-primary hover:underline" data-testid="expense-more">
          Breakdown →
        </Link>
      </div>
      <div className="mt-3 flex items-baseline gap-3">
        <div className="font-mono text-3xl font-black tabular-nums text-foreground">{fmtRs(total)}</div>
        <div className={"text-xs font-bold " + zoneCls}>{percent}% of sales</div>
      </div>
      {/* Horizontal bar comparing expense to sales */}
      <div className="mt-3 h-2 overflow-hidden rounded-full bg-secondary">
        <div
          className={
            "h-full rounded-full transition-all duration-700 " +
            (zone === "ok" ? "bg-gradient-to-r from-emerald-400 to-emerald-500" : zone === "warn" ? "bg-gradient-to-r from-amber-400 to-orange-500" : "bg-gradient-to-r from-red-400 to-red-500")
          }
          style={{ width: `${Math.min(percent * 4, 100)}%` }}
        />
      </div>
      <div className="mt-1 flex justify-between text-[9px] uppercase tracking-widest text-muted-foreground">
        <span>{t("dealer.dealerhero.healthy_zone")}</span>
        <span>&lt; 15% of sales</span>
      </div>
    </div>
  );
}

function CreditCustomersCard({ customers = [], total }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-sm" data-testid="credit-customers-card">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-sky-500/15 text-sky-700 dark:text-sky-400">
            <Users className="h-4 w-4" />
          </div>
          <div className="text-sm font-bold text-foreground">{t("manager.top_credit_customers")}</div>
        </div>
        <Link to="/dealer/credit-customers" className="text-[11px] font-semibold text-primary hover:underline" data-testid="credit-more">
          All →
        </Link>
      </div>
      {customers.length === 0 ? (
        <div className="mt-4 rounded-lg border border-dashed border-border bg-secondary/30 p-4 text-center text-xs text-muted-foreground">
          No credit sales yet — customers will show here.
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          {customers.map((c, i) => (
            <div key={c.customer_id || c.name || i} className="flex items-center justify-between gap-2 text-sm">
              <div className="flex min-w-0 items-center gap-2">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-black tabular-nums text-primary">
                  {i + 1}
                </div>
                <div className="truncate font-medium text-foreground">{c.name || c.customer_name || "Unknown"}</div>
              </div>
              <div className="shrink-0 font-mono text-sm font-bold tabular-nums text-foreground">{fmtRs(c.amount)}</div>
            </div>
          ))}
          <div className="mt-2 border-t border-border pt-2 text-[11px] text-muted-foreground">
            <b className="text-foreground">Total credit · 30d:</b> {fmtRs(total)}
          </div>
        </div>
      )}
    </div>
  );
}
